import React from 'react';
import { Link } from 'react-router-dom';
import { Quote, MapPin, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';

const testimonials = [
  {
    id: 1,
    name: "Lakshmi Kishore",
    location: "Kochi, Kerala",
    message: "I've been using Agniya Spices chilli powder for over a year now. The color and aroma it brings to my sambar and fish curry is unmatched. My whole family loves the authentic taste!",
    image: "https://images.unsplash.com/photo-1759840278381-bf7d5e332050?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjB3b21hbiUyMHNtaWxpbmclMjBwb3J0cmFpdHxlbnwwfHx8fDE3Njk2ODYxMjB8MA&ixlib=rb-4.1.0&q=85"
  },
  {
    id: 2,
    name: "Chef Sanjay Ramankulath",
    location: "Thrissur, Kerala",
    message: "As a restaurant Chef, consistency in spices is crucial. Agniya Spices delivers the same quality every single time. My customers always compliment the rich color of our dishes.",
    image: "https://images.pexels.com/photos/30660319/pexels-photo-30660319.jpeg"
  },
  {
    id: 3,
    name: "Gayathri",
    location: "Thrissur, Kerala",
    message: "What I love most about Agniya is that there's no artificial color. I can cook for my children knowing the spices are pure and safe. The turmeric powder is so fresh and aromatic!",
    image: "https://images.unsplash.com/photo-1759840278511-f73a3d62fb9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjB3b21hbiUyMHNtaWxpbmclMjBwb3J0cmFpdHxlbnwwfHx8fDE3Njk2ODYxMjB8MA&ixlib=rb-4.1.0&q=85"
  },
  {
    id: 4,
    name: "Sibin E Sidharthan",
    location: "Thrissur, Kerala",
    message: "In our daily life we prefer quality spices. Agniya Spices has been our trusted partner for daily use & for all major events. The coriander powder adds perfect flavor to our biryanis!",
    image: "https://images.unsplash.com/photo-1664101606938-e664f5852fac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGluZGlhbiUyMGN1c3RvbWVyJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzY5Njg2MTEwfDA&ixlib=rb-4.1.0&q=85"
  },
  {
    id: 5,
    name: "Jithin Venugopalan Nambiar",
    location: "Kannur, Kerala",
    message: "The quality of Agniya Spices is exceptional. I use their products daily in my home kitchen, and the freshness of the spices makes a noticeable difference in every meal. Highly recommended!",
    image: "https://images.unsplash.com/photo-1595956481935-a9e254951d49?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGluZGlhbiUyMGN1c3RvbWVyJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzY5Njg2MTEwfDA&ixlib=rb-4.1.0&q=85"
  }
];

const TestimonialsSlider = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-[#f7f5f2] to-[#ebe7e2]" data-testid="testimonials-slider-section">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#8B2820] mb-4">
            What Our Customers Say
          </h2>
          <p className="text-lg text-[#4a3a33] max-w-2xl mx-auto">
            Trusted by households, restaurants, and caterers across Kerala
          </p>
        </div>

        {/* Carousel */}
        <div className="max-w-6xl mx-auto px-12">
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {testimonials.map((testimonial) => (
                <CarouselItem key={testimonial.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 bg-white h-full">
                    <CardContent className="p-6">
                      {/* Quote Icon */}
                      <div className="mb-4">
                        <div className="inline-flex p-2 bg-[#8B2820]/10 rounded-full">
                          <Quote className="h-5 w-5 text-[#8B2820]" />
                        </div>
                      </div>

                      {/* Message */}
                      <p className="text-[#4a3a33] text-sm leading-relaxed mb-6 line-clamp-4 italic">
                        "{testimonial.message}"
                      </p>

                      {/* Customer Info */}
                      <div className="flex items-center mt-auto">
                        <img 
                          src={testimonial.image} 
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-[#8B2820]/20"
                        />
                        <div className="ml-3">
                          <h4 className="font-semibold text-[#4a3a33] text-sm">{testimonial.name}</h4>
                          <div className="flex items-center text-[#736c64] text-xs mt-0.5">
                            <MapPin className="h-3 w-3 mr-1" />
                            <span>{testimonial.location}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="bg-white hover:bg-[#8B2820] hover:text-white border-[#8B2820]/20 -left-6" />
            <CarouselNext className="bg-white hover:bg-[#8B2820] hover:text-white border-[#8B2820]/20 -right-6" />
          </Carousel>
        </div>

        {/* View All Link */}
        <div className="text-center mt-10">
          <Link to="/testimonials">
            <Button 
              variant="outline"
              className="border-[#8B2820] text-[#8B2820] hover:bg-[#8B2820] hover:text-white px-8 py-5 text-base transition-all duration-300"
              data-testid="view-all-testimonials-btn"
            >
              View All Testimonials
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSlider;
